/**
 * RMT — marketplace de alz.
 * Endpoints de admin (/admin/rmt/*) e do vendedor logado (/rmt/*).
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './api';

/* ───────────────────────────── Admin ───────────────────────────── */

export interface RmtSellLimits {
  limitAlzDay: number;
  limitAlzWeek: number;
  limitAlzMonth: number;
  limitSalesDay: number;
  limitSalesWeek: number;
  limitSalesMonth: number;
}

export interface RmtItemConfig {
  itemsEnabled: boolean;
  alzPriceMinBrl: number;
  alzPriceMaxBrl: number;
  itemPriceMinBrl: number;
  itemPriceMaxBrl: number;
}

export interface RmtConfig extends RmtSellLimits, RmtItemConfig {
  cofreCharIdx: number | null;
  cofreCharName: string | null;
  depositEnabled: boolean;
  minDepositAlz: number;
}

export interface RmtConfigUpdate extends RmtSellLimits, RmtItemConfig {
  depositEnabled: boolean;
  minDepositAlz: number;
}

export interface RmtBlacklistItem {
  blacklistId: number;
  itemId: number;
  itemOption: number | null;
  reason: string | null;
  createdAt: string | null;
}

export interface RmtPriceLimit {
  itemId: number;
  minPriceBrl: number;
  maxPriceBrl: number;
  note: string | null;
}

export interface RmtSeller {
  userNum: number;
  accountId: string | null;
  sellerName: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  docStatus: string;   // pending | approved | rejected
  status: string;      // active | suspended
  balanceAlz: number;
  grantedBy: number | null;
  grantedById: string | null;
  grantedAt: string | null;
  notes: string | null;
}

export interface RmtAccountSearch {
  userNum: number;
  accountId: string;
  email: string | null;
  isSeller: boolean;
}

export interface RmtCharSearch {
  characterIdx: number;
  name: string;
  userNum: number;
  online: boolean;
}

export interface RmtScanResult {
  enabled: boolean;
  processed: number;
  totalAlz: number;
}

export interface RmtSellerUpdate {
  status?: string;
  docStatus?: string;
  notes?: string;
}

export const getRmtConfig = () => apiGet<RmtConfig>('/admin/rmt/config');

export const updateRmtConfig = (input: RmtConfigUpdate) =>
  apiPut<RmtConfig>('/admin/rmt/config', input);

export const setRmtCofre = (characterIdx: number) =>
  apiPost<RmtConfig>('/admin/rmt/config/cofre', { characterIdx });

export const searchRmtChar = (q: string) =>
  apiGet<RmtCharSearch[]>('/admin/rmt/char-search', { q });

export const runRmtScan = () => apiPost<RmtScanResult>('/admin/rmt/scan', {});

export const listRmtSellers = () => apiGet<RmtSeller[]>('/admin/rmt/sellers');

export const searchRmtAccount = (q: string) =>
  apiGet<RmtAccountSearch[]>('/admin/rmt/sellers/search', { q });

export const grantRmtSeller = (userNum: number, notes?: string) =>
  apiPost<RmtSeller>(`/admin/rmt/sellers/${userNum}`, { notes });

export const updateRmtSeller = (userNum: number, input: RmtSellerUpdate) =>
  apiPatch<RmtSeller>(`/admin/rmt/sellers/${userNum}`, input);

export const revokeRmtSeller = (userNum: number) =>
  apiDelete<{ status: string; message: string }>(`/admin/rmt/sellers/${userNum}`);

export const getRmtBlacklist = () => apiGet<RmtBlacklistItem[]>('/admin/rmt/blacklist');

export const addRmtBlacklist = (itemId: number, itemOption: number | null, reason: string) =>
  apiPost<RmtBlacklistItem>('/admin/rmt/blacklist', { itemId, itemOption, reason: reason || undefined });

export const removeRmtBlacklist = (blacklistId: number) =>
  apiDelete<{ status: string }>(`/admin/rmt/blacklist/${blacklistId}`);

export const getRmtPriceLimits = () => apiGet<RmtPriceLimit[]>('/admin/rmt/price-limits');

export const upsertRmtPriceLimit = (
  itemId: number,
  input: { minPriceBrl: number; maxPriceBrl: number; note?: string },
) => apiPut<RmtPriceLimit>(`/admin/rmt/price-limits/${itemId}`, input);

export const removeRmtPriceLimit = (itemId: number) =>
  apiDelete<{ status: string }>(`/admin/rmt/price-limits/${itemId}`);

/* ─────────────────────────── Vendedor ──────────────────────────── */

export interface RmtMe {
  isSeller: boolean;
  status: string | null;
  balanceAlz: number;
  availableAlz: number;
  alzPriceBrl: number;
  sellerName: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  docStatus: string | null;
  cofreCharName: string | null;
  minDepositAlz: number;
  throttled: boolean;
  throttleReason: string | null;
}

export interface RmtDeposit {
  depositId: number;
  mailId: number;
  senderCharIdx: number;
  senderName: string | null;
  alzAmount: number;
  mailDate: string | null;
  detectedAt: string;
}

export interface RmtLedgerEntry {
  ledgerId: number;
  entryType: string;   // deposit | sale | withdrawal | adjustment
  alzDelta: number;
  balanceAfter: number;
  refType: string | null;
  refId: number | null;
  createdAt: string;
  notes: string | null;
}

export interface RmtSellerConfigUpdate {
  sellerName?: string;
  pixKey?: string;
  pixKeyType?: string;
}

export const getRmtMe = () => apiGet<RmtMe>('/rmt/me');

/** Dispara o scan de deposito on-demand e devolve o status atualizado. */
export const refreshRmt = () => apiPost<RmtMe>('/rmt/refresh', {});

export const getRmtDeposits = (limit = 100) =>
  apiGet<RmtDeposit[]>('/rmt/deposits', { limit });

export const getRmtLedger = (limit = 100) =>
  apiGet<RmtLedgerEntry[]>('/rmt/ledger', { limit });

export const updateRmtSellerConfig = (input: RmtSellerConfigUpdate) =>
  apiPut<RmtMe>('/rmt/config', input);

/* ─────────────────────── Marketplace (compra) ──────────────────── */

export type RmtPeriod = '7d' | '30d' | '1y';

export interface RmtStatPoint {
  label: string;
  price: number;
}

export interface RmtMarketStats {
  period: string;
  points: RmtStatPoint[];
  alzInOffer: number;
  minPrice: number;
  maxPrice: number;
  oscillationPct: number;
  unitAlz: number;
  unitPriceBrl: number;
}

export interface RmtBuyResult {
  orderId: number;
  units: number;
  alzAmount: number;
  charName: string;
  vendorName: string;
  priceBrl: number;
  message: string;
}

export interface RmtVendor {
  userNum: number;
  sellerName: string;
  availableAlz: number;
  alzPriceBrl: number;
}

export interface RmtItemMarket {
  listingId: number;
  baseItemId: number | null;
  itemName: string | null;
  upgrade: number;
  extreme: number;
  divine: number;
  slotCount: number;
  bound: boolean;
  sealed: boolean;
  priceBrl: number;
  sellerUserNum: number;
  sellerName: string;
  depositedAt: string;
}

export interface RmtBuyItemResult {
  listingId: number;
  itemName: string;
  charName: string;
  vendorName: string;
  priceBrl: number;
  message: string;
}

export interface RmtOrder {
  orderId: number;
  units: number;
  alzAmount: number;
  priceBrl: number;
  charName: string | null;
  status: string;
  isSandbox: boolean;
  createdAt: string;
  deliveredAt: string | null;
}

export const getMarketStats = (period: RmtPeriod) =>
  apiGet<RmtMarketStats>('/rmt/stats', { period });

export const getVendors = () => apiGet<RmtVendor[]>('/rmt/vendors');

export const buyAlz = (units: number, charIdx: number, vendorUserNum: number) =>
  apiPost<RmtBuyResult>('/rmt/buy', { units, charIdx, vendorUserNum });

export const getMyOrders = (limit = 50) =>
  apiGet<RmtOrder[]>('/rmt/orders', { limit });

/** Itens à venda no marketplace. */
export const getItems = () => apiGet<RmtItemMarket[]>('/rmt/items');

/** Compra um item, entregue no personagem escolhido. */
export const buyItem = (listingId: number, charIdx: number) =>
  apiPost<RmtBuyItemResult>('/rmt/buy-item', { listingId, charIdx });

/* ──────────────────── Vendedor — itens e alz à venda ──────────────────── */

export interface RmtItemListing {
  listingId: number;
  baseItemId: number | null;
  itemName: string | null;
  itemKindIdx: number;
  itemOption: number;
  itemDurationIdx: number;
  upgrade: number;
  extreme: number;
  divine: number;
  slotCount: number;
  bound: boolean;
  sealed: boolean;
  priceBrl: number;
  enabled: boolean;
  status: string;
  blacklisted: boolean;
  depositedAt: string;
}

/** Vendedor define quanto alz fica à venda e o preço por 100kk. */
export const setSellerAlz = (availableAlz: number, alzPriceBrl: number) =>
  apiPut<RmtMe>('/rmt/seller/alz', { availableAlz, alzPriceBrl });

/** Itens depositados pelo vendedor (pra precificar/habilitar). */
export const getSellerItems = () => apiGet<RmtItemListing[]>('/rmt/seller/items');

/** Define preço (BRL) e habilita/desabilita um item. */
export const updateItemListing = (listingId: number, priceBrl: number, enabled: boolean) =>
  apiPut<RmtItemListing>(`/rmt/seller/items/${listingId}`, { priceBrl, enabled });
